import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cutlery, Truck, Barcode, Map, ArrowRight, Camera, Bag, NavArrowDown, Twitter, Instagram, ChatLines, Calendar, List } from 'iconoir-react';
import { getLojaInfo } from '../services/api';
import SEOMetaTags from '../components/SEOMetaTags';
import '../components/Loja.css';

const Loja = ({ brand }) => {
  const [loja, setLoja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [showHorarios, setShowHorarios] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    fetchLojaData();
  }, [brand]);

  const fetchLojaData = async () => {
    try {
      const response = await getLojaInfo(brand);
      const lojaData = response.loja;
      setLoja(lojaData);
      checkIfOpen(lojaData);
      
      if (lojaData.galeria && Array.isArray(lojaData.galeria) && lojaData.galeria.length > 0) {
        const randomIndex = Math.floor(Math.random() * lojaData.galeria.length);
        setBackgroundImage(`url(https://grubupload.s3.amazonaws.com/${lojaData.galeria[randomIndex].url})`);
      } else if (lojaData.logo_url) {
        setBackgroundImage(`url(https://grubupload.s3.amazonaws.com/${lojaData.logo_url})`);
      } else {
        setBackgroundImage('#000000');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const checkIfOpen = (lojaData) => {
    if (!lojaData?.horarios || lojaData.horarios.length === 0) {
      return;
    }

    try {
      const today = new Date();
      const diasSemana = [1, 2, 4, 8, 16, 32, 64];
      const diaAtual = diasSemana[today.getDay()];
      
      const horarioHoje = lojaData.horarios.find(h => h.dia === diaAtual && h.ativo);
      
      if (!horarioHoje) {
        setIsOpen(false);
        return;
      }

      const [hiHora, hiMin] = horarioHoje.horario_inicial.split(':').map(Number);
      const [hfHora, hfMin] = horarioHoje.horario_final.split(':').map(Number);
      
      const horaAtual = today.getHours() * 60 + today.getMinutes();
      const horaInicio = hiHora * 60 + hiMin;
      const horaFim = hfHora * 60 + hfMin;

      if (horaAtual >= horaInicio && horaAtual <= horaFim) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    } catch (error) {
      setIsOpen(false);
    }
  };

  const handleLocationClick = () => {
    if (loja?.endereco) {
      const { logradouro, numero, cidade } = loja.endereco;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${logradouro}, ${cidade}, ${numero}&travelmode=driving`;
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div className="container py-5 d-flex justify-content-center align-items-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!loja) {
    return (
      <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div className="container py-5 d-flex justify-content-center align-items-center">
          <div className="text-center">
            <h6 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>Loja não encontrada</h6>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOMetaTags loja={loja} brand={brand} />
      <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div className="container py-3">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="mb-3" style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '120px',
                  backgroundImage: backgroundImage || 'none',
                  backgroundColor: '#000000',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  backgroundSize: 'cover',
                  filter: loja.galeria && loja.galeria.length > 0 ? 'none' : 'blur(8px)'
                }} />
                <div className="p-3 text-center" style={{ position: 'relative' }}>
                  {loja.logo_url && (
                    <img 
                      src={`https://grubupload.s3.amazonaws.com/${loja.logo_url}`}
                      alt={loja.nome}
                      className="mb-3"
                      style={{ maxHeight: '96px', borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '49px' }}
                    />
                  )}

                  {loja.tipo && (
                    <div className="mb-2">
                      <span className="badge bg-lightgray text-uppercase text-black" style={{ letterSpacing: '0.03em' }}>{loja.tipo}</span>
                    </div>
                  )}

                  <h2 className="mb-3" style={{ fontWeight: 700, color: '#1a1a1a' }}>{loja.nome}</h2>

                  {loja.endereco && (
                    <div className="mb-3">
                      <p className="mb-2" style={{ fontSize: '0.95rem', color: '#4b5563' }}>
                        {loja.endereco.logradouro} {loja.endereco.numero}
                        {loja.endereco.complemento && ` ${loja.endereco.complemento}`}
                        <br />
                        {loja.endereco.bairro}
                        {loja.endereco.cidade && ` - ${loja.endereco.cidade}`}
                      </p>
                    </div>
                  )}

                  {loja.horarios && loja.horarios.length > 0 && (
                    <div className="mb-3 text-center">
                      <div className="mb-2">
                        {isOpen ? (
                          <span className="badge bg-success rounded-pill status-badge">ABERTO</span>
                        ) : (
                          <span className="badge bg-danger rounded-pill status-badge">FECHADO</span>
                        )}
                      </div>
                      <button 
                        className="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center justify-content-center" 
                        style={{ fontSize: '0.85rem', color: '#6b7280', gap: '4px' }}
                        onClick={() => setShowHorarios(!showHorarios)}
                      >
                        Veja os horários de funcionamento
                        <NavArrowDown 
                          width={16} 
                          height={16} 
                          style={{ 
                            transform: showHorarios ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                          }} 
                        />
                      </button>
                      
                      {showHorarios && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                          <div className="d-flex flex-column" style={{ gap: '8px' }}>
                            {loja.horarios.map((horario, index) => {
                              const dias = { 1: 'Domingo', 2: 'Segunda', 4: 'Terça', 8: 'Quarta', 16: 'Quinta', 32: 'Sexta', 64: 'Sábado' };
                              return (
                                <div key={index} className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.9rem' }}>
                                  <span style={{ color: '#4b5563', fontWeight: 500 }}>{dias[horario.dia]}</span>
                                  <span style={{ color: horario.ativo ? '#1a1a1a' : '#9ca3af', fontWeight: 600 }}>
                                    {horario.ativo ? `${horario.horario_inicial.substring(0, 5)} - ${horario.horario_final.substring(0, 5)}` : 'Fechado'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 mb-3" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <h6 className="mb-3" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>Links de Interesse</h6>
                <div className="d-grid gap-2">
                  <Link to="/" className="text-decoration-none">
                    <div className="d-flex align-items-center justify-content-between loja-link-item">
                      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <Cutlery className="text-dark" width={20} height={20} />
                        <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Ver Cardápio</span>
                      </div>
                      <ArrowRight className="text-muted" width={20} height={20} />
                    </div>
                  </Link>

                  <Link to="/reserva" className="text-decoration-none">
                    <div className="d-flex align-items-center justify-content-between loja-link-item">
                      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <Calendar className="text-dark" width={20} height={20} />
                        <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Reservar Mesa</span>
                      </div>
                      <ArrowRight className="text-muted" width={20} height={20} />
                    </div>
                  </Link>

                  {loja.configuracoes?.b_fila === 1 && (
                  <Link to="/fila" className="text-decoration-none">
                    <div className="d-flex align-items-center justify-content-between loja-link-item">
                      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <List className="text-dark" width={20} height={20} />
                        <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Entrar na Fila de Atendimento</span>
                      </div>
                      <ArrowRight className="text-muted" width={20} height={20} />
                    </div>
                  </Link>
                  )}

                  {loja.configuracoes?.b_delivery === 1 && (
                    <Link to="/delivery" className="text-decoration-none">
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <Truck className="text-dark" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Pedir no Delivery</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </Link>
                  )}

                  {loja.configuracoes?.b_balcao === 1 && (
                    <Link to="/balcao" className="text-decoration-none">
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <Bag className="text-dark" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Pedir no Balcão</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </Link>
                  )}

                  <Link to="/qr-scanner" className="text-decoration-none">
                    <div className="d-flex align-items-center justify-content-between loja-link-item">
                      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <Camera className="text-dark" width={20} height={20} />
                        <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Ler QR Code</span>
                      </div>
                      <ArrowRight className="text-muted" width={20} height={20} />
                    </div>
                  </Link>

                  {loja.endereco && (
                    <button className="w-100 p-0 border-0 bg-transparent" onClick={handleLocationClick}>
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <Map className="text-dark" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Como Chegar</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </button>
                  )}

                  {loja.whatsapp && (
                    <a href={`https://api.whatsapp.com/send?phone=55${loja.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <ChatLines className="text-dark" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Fale conosco no WhatsApp</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </a>
                  )}

                  {loja.instagram && (
                    <a href={loja.instagram} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <Instagram className="text-dark" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Siga a gente no Instagram</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </a>
                  )}

                  {loja.twitter && (
                    <a href={loja.twitter} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      <div className="d-flex align-items-center justify-content-between loja-link-item">
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <img src="https://grubicons.s3.us-east-1.amazonaws.com/IFood_logo.svg" alt="iFood" width={20} height={20} />
                          <span style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>Estamos também no iFood</span>
                        </div>
                        <ArrowRight className="text-muted" width={20} height={20} />
                      </div>
                    </a>
                  )}
                </div>
              </div>

              <div className="p-3 mb-3 text-center" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <a 
                  href="https://grub.digital" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <img 
                    src="https://grubicons.s3.us-east-1.amazonaws.com/powered-by-grub.png"
                    alt="Powered by Grub"
                    style={{ maxWidth: '200px', width: '100%' }}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Loja;
